/**
 * NAV-BUTTONS (die zwei runden Weiter/Zurück-Knöpfe unten rechts) — seit der
 * ersten Scrolly-Version dabei, in der neuen Plattform aber nie gerendert.
 * Portiert 09.08.2026 (Jans + Benedikts Aufgabenliste): Pfeile zeigen jetzt
 * HOCH/RUNTER (scrollygerecht) und stehen übereinander.
 *
 * EINE Auflöse-Funktion für alle Konsumenten (Voll-Reader, Bühnen-Dialog,
 * Template-Editor-Vorschau) — Quelle sind die METADATEN, wie bei Titel und
 * Vorhang (der Pro-Editor spiegelt seine Startbildschirm-Felder beim
 * Speichern dorthin, s. cover-meta.ts in app/):
 *
 *   icons_color / iconsColor        → Pfeilfarbe
 *   icons_bg_color / iconsBgColor   → Knopf-Hintergrund
 *   hide_nav_buttons / hideNavButtons → true = Knöpfe generell aus (neu)
 *
 * Farbwerte kommen in drei Formen an: {r,g,b,a}-Objekt (Pro-Editor),
 * Hex-String (Template-Editor) oder ein Legacy-Palettenname („grey-marl",
 * Jans 1.0 speicherte Theme-SCHLÜSSEL statt Farben). Alle drei werden hier
 * verstanden; Unlesbares fällt auf Weiß-auf-Schwarz zurück (die
 * Pro-Editor-Vorgabe).
 */

/** Legacy-Palette aus Jans 1.0 (COLORS.customThemeColor) — Schlüssel wie sie
 *  in Alt-Metadaten stehen. Nur für die Rückwärts-Auflösung alter Scrollys. */
const LEGACY_PALETTE: Record<string, string> = {
    purple: '#6666FF',
    pink: '#FF004D',
    mint: '#00FFD4',
    yellow: '#FFFF00',
    navy: '#05234B',
    olive: '#142D23',
    graphite: '#203441',
    aubergine: '#3C1E46',
    black: '#000000',
    'grey-marl': '#3C3C3C',
    greymarl: '#3C3C3C',
    gray: '#CCCCCC',
    'off-white': '#F5F5F5',
    offwhite: '#F5F5F5',
    white: '#FFFFFF',
};

export interface NavButtonsConfig {
    /** Pfeilfarbe als CSS-Farbe. */
    arrowColor: string;
    /** Knopf-Hintergrund als CSS-Farbe. */
    bgColor: string;
}

/** Eine Metadaten-Farbe ({r,g,b,a} | Hex | rgb() | Legacy-Schlüssel) → CSS-Farbe. */
function toCssColor(value: unknown, fallback: string): string {
    if (value && typeof value === 'object') {
        const c = value as { r?: unknown; g?: unknown; b?: unknown; a?: unknown };
        if (
            typeof c.r === 'number' &&
            typeof c.g === 'number' &&
            typeof c.b === 'number'
        ) {
            const a = typeof c.a === 'number' ? c.a : 1;
            return `rgba(${c.r}, ${c.g}, ${c.b}, ${a})`;
        }
        return fallback;
    }
    if (typeof value !== 'string') return fallback;
    const s = value.trim();
    if (!s) return fallback;
    if (/^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?([0-9a-fA-F]{2})?$/.test(s)) return s;
    if (/^rgba?\(/i.test(s)) return s;
    const legacy = LEGACY_PALETTE[s.toLowerCase()];
    if (legacy) return legacy;
    return fallback;
}

/**
 * Liest die Nav-Button-Einstellungen aus den Post-METADATEN (beide
 * Schreibweisen). Gibt `null` zurück, wenn die Knöpfe ausgeschaltet sind
 * (hide_nav_buttons) — ohne Angabe sind sie AN, wie im Legacy-System
 * („schon immer drin"). Die GRÖSSEN-Regel (erst ab iPad-Breite und im
 * Querformat) liegt beim Renderer, der die Bühnenmaße kennt.
 */
export function resolveNavButtons(
    metadata: Record<string, unknown> | null | undefined,
): NavButtonsConfig | null {
    const m = metadata ?? {};
    const hidden = m.hideNavButtons ?? m.hide_nav_buttons;
    if (hidden === true) return null;
    return {
        arrowColor: toCssColor(m.iconsColor ?? m.icons_color, '#FFFFFF'),
        bgColor: toCssColor(m.iconsBgColor ?? m.icons_bg_color, '#000000'),
    };
}
